import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type FloorBox, type FloorItemCard } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { colourLabel, flowerTypeLabel } from "../../i18n/taxonomy";
import { t as translate } from "../../i18n/t";
import { uomLabel } from "./shopFloorFormat";
import { PhotoInput } from "../PhotoInput";
import { isShelfZone } from "../../utils/shelfZones";
import { ShopFloorItemBarcodePrint } from "./ShopFloorItemBarcodePrint";
import { itemStickerPrinter, printableBarcode, printItemSticker } from "./printItemSticker";
import { itemStickerOutcomeText } from "./itemStickerOutcome";
import { lotCostFigure } from "./kioskCostDisplay";
import { invoiceTotalFromUnitPrice } from "../../utils/receiveTotals";
import { basisAfterEdit, basisForPrefill } from "../../utils/typedMoneyBasis";
import type { ReceivedBatchMoney, TypedMoneyBasis } from "../../api/types";
import { cost, dateText, money, percentText } from "../../i18n/intl";
import { useDismissOnEscape } from "../../hooks/useDismissOnEscape";

/** G-174: the supplier choice that keeps a priced receive out of any delivery. */
const NOT_ON_LIST = "not-on-the-list";

const BarcodeScanner = lazy(() =>
  import("../BarcodeScanner").then((m) => ({ default: m.BarcodeScanner })),
);

/**
 * G-73 — the kiosk item card.
 *
 * A florist taps a row in "See stock" (or scans a barcode) and gets the whole
 * item in front of them: the big photo, everything the catalogue knows, how
 * much is in this shop and where, and what arrived recently. The things they
 * can fix while holding the product are here too, because the moment you are
 * holding it is the moment you can see that the photo is wrong, the barcode
 * changed, or the shelf does not match the number.
 *
 * Rendered through a PORTAL, and that is load-bearing rather than tidiness:
 * the panel this card is opened from (`.shop-floor-stock-panel`) carries a
 * `backdrop-filter`, which makes it the containing block for every
 * `position: fixed` descendant. Left in place, the "full screen" overlay is
 * trapped inside the panel — at 375 px it rendered 203 px wide with the item
 * name cut in half. `SaleConfirmationOverlay` portals for the same reason.
 *
 * WHO may change WHAT is not decided here. The server sends
 * `permissions.editableFields`, read from `floor-item-edit-policy.ts`, and
 * this screen renders inputs for exactly those — a second copy of that rule
 * in the UI is a rule that drifts. The server refuses the rest regardless.
 */

type Panel =
  | "none"
  | "edit"
  | "quantity"
  | "scan"
  /** G-86: the item exists but this shop holds none — take some in. */
  | "receive"
  /** G-84 follow-up: the item's own barcode, on a sticker. G-100: the
      FALLBACK only — with a printer paired, one tap prints and this never
      opens. */
  | "print-barcode"
  /** G-84 (OD-50): scan the box label, then say how many. */
  | "put-box-scan"
  | "put-box-notbox"
  | "put-box-qty";

/** A box barcode is `BX` + digits — anything else scanned here is not a box. */
function hasStatus(err: unknown): err is { status: number } {
  return typeof err === "object" && err !== null && "status" in err &&
    typeof (err as { status: unknown }).status === "number";
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dateText(d);
}

export interface ShopFloorItemCardProps {
  itemId: string;
  onClose(): void;
  /** Called after any successful write, so the list behind can re-read. */
  onChanged?(): void;
}

export function ShopFloorItemCard({
  itemId,
  onClose,
  onChanged,
}: ShopFloorItemCardProps) {
  const { t } = useT();
  const [card, setCard] = useState<FloorItemCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>("none");
  const [busy, setBusy] = useState(false);
  // G-99: SEPARATE from `busy`, which every write on this card sets. Sharing
  // it made the Assign button read "Assigning…" while a PHOTO was uploading —
  // the tablet claiming something that was not happening.
  const [assigning, setAssigning] = useState(false);
  /**
   * G-99: the code this card just minted, kept in case the re-read that
   * follows cannot reach the server. Without it a successful issue on a bad
   * connection leaves the card with a stale `issuedBarcode: null`, no print
   * panel and no action row — every button gone until the card is reopened.
   */
  const [justIssued, setJustIssued] = useState<string | null>(null);
  /**
   * G-100: a THIRD flag, and the reason is the one G-99 already paid for.
   * `busy` belongs to `run` — every write on this card sets it — and
   * `assigning` belongs to the issue call. A print is neither, and it lasts
   * three to six seconds, so a button borrowing someone else's flag would
   * spend that time claiming something that is not happening.
   */
  const [printing, setPrinting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Edit panel
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [priceDraft, setPriceDraft] = useState("");

  // Quantity panel
  const [zoneDraft, setZoneDraft] = useState("");
  const [qtyDraft, setQtyDraft] = useState("");
  const [reasonDraft, setReasonDraft] = useState("");

  // G-84: put in the box — the scanned box, how many, and from which shelf.
  const [putBox, setPutBox] = useState<FloorBox | null>(null);
  const [putQty, setPutQty] = useState("1");
  const [putZoneId, setPutZoneId] = useState("");
  const [putScanned, setPutScanned] = useState<string | null>(null);

  // G-86 receive panel: how many arrived, and what one of them cost.
  const [recvZoneDraft, setRecvZoneDraft] = useState("");
  const [recvQtyDraft, setRecvQtyDraft] = useState("");
  const [recvUnitPriceDraft, setRecvUnitPriceDraft] = useState("");
  /** G-121 S3 (OD-138/OD-140): the price's side of IVA — none until picked. */
  const [recvBasisDraft, setRecvBasisDraft] = useState<TypedMoneyBasis | null>(null);
  /**
   * G-174 (KI-100, owner 2026-10-22): who sold it, asked with a price — a
   * supplier id, NOT_ON_LIST, or "" until the manager picks.
   */
  const [recvSupplierDraft, setRecvSupplierDraft] = useState("");
  const [floorSuppliers, setFloorSuppliers] = useState<Array<{ id: string; name: string }> | null>(null);
  const [suppliersFailed, setSuppliersFailed] = useState(false);

  const photoUrlRef = useRef<string | null>(null);

  const revokePhoto = useCallback(() => {
    if (photoUrlRef.current) {
      URL.revokeObjectURL(photoUrlRef.current);
      photoUrlRef.current = null;
    }
    setPhotoUrl(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await shopModeApi.floorItemCard(itemId);
      setCard(next);
      setDescriptionDraft(next.item.description ?? "");
      setNameDraft(next.item.name);
      setPriceDraft(next.item.defaultSaleUnitPriceEur ?? "");
      setZoneDraft(next.stock.zones[0]?.zoneId ?? "");
      // A photo the item may no longer have must not linger from the last read.
      revokePhoto();
      if (next.item.imageKey) {
        const url = await shopModeApi.floorItemImageBlobUrl(itemId, {
          size: "full",
          version: next.item.imageKey,
        });
        if (url) {
          photoUrlRef.current = url;
          setPhotoUrl(url);
        }
      }
    } catch (err: unknown) {
      setError(errorText(err, "itemCard.couldNotLoad"));
    } finally {
      setLoading(false);
    }
  }, [itemId, revokePhoto]);

  useEffect(() => {
    void load();
  }, [load]);

  // A different item must never inherit the last one's freshly issued code.
  useEffect(() => setJustIssued(null), [itemId]);

  /**
   * G-100: a print outlives the card if the florist closes it mid-job —
   * `printRaster` takes seconds — so the result must not be written into a
   * component that is gone. The printer is not orphaned by this: `printRaster`
   * disconnects in its own `finally` whatever happens here.
   */
  const onScreen = useRef(true);
  useEffect(() => {
    onScreen.current = true;
    return () => {
      onScreen.current = false;
    };
  }, []);

  /**
   * The print latch, and it is a REF rather than the `printing` state above
   * because a state read inside a handler is the value from ITS render.
   *
   * The window is real, not theoretical: the assign path flips the button to
   * "Print barcode" — enabled, `printing` still false in that render — and
   * only then starts printing. A florist tapping it in that moment passed a
   * state guard and started a SECOND job, and `printRaster` has no guard of
   * its own: both calls connect to the same device, interleave their writes,
   * and whichever finishes first disconnects out from under the other. One
   * torn sticker and an error about a print that did happen.
   */
  const printingRef = useRef(false);

  useEffect(() => revokePhoto, [revokePhoto]);

  /*
   * G-106 — one stack, not one listener each: a box card opens an item card
   * over itself (`onOpenItem`), and two plain listeners closed both on one
   * press.
   */
  useDismissOnEscape(onClose);

  /** Every write goes through here: one busy flag, one error line, one re-read. */
  const run = useCallback(
    /**
     * `doneWords` may be worked out AFTER the write (G-121 S3: the receive's
     * notice says what the server stored), so it can be a function.
     */
    async (work: () => Promise<void>, doneWords: string | (() => string)) => {
      setBusy(true);
      setActionError(null);
      setNotice(null);
      try {
        await work();
        setPanel("none");
        setNotice(typeof doneWords === "function" ? doneWords() : doneWords);
        await load();
        onChanged?.();
      } catch (err: unknown) {
        setActionError(
          errorText(err, "itemCard.didNotGoThrough"),
        );
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged],
  );

  /**
   * G-100 — the whole of "Print barcode": one tap, and paper comes out.
   *
   * Owner, 2026-09-22. The old first tap only opened a panel whose own button
   * did the printing, and the florist had to scroll down to it — two taps and
   * two scrolls for one sticker.
   *
   * THE PANEL SURVIVES AS THE FALLBACK, for the one case a tap cannot answer:
   * with nothing paired, printing is impossible and PAIRING needs a person
   * choosing a device from a list. So a printer is resolved first, and only its
   * absence opens the old screen — where `PairPrinterPrompt` can fix exactly
   * that, and print once from there.
   *
   * RESOLVED PER TAP rather than held in state: it is a `localStorage` read,
   * so a printer paired on another screen a minute ago is found by this press.
   *
   * No preview. That is the trade the owner chose and it is a real one — a
   * long name prints truncated and there is no longer a moment to see it
   * coming. The answer is that the paper IS the preview: it is in your hand
   * three seconds later, and a wrong sticker costs a reprint, which G-99 made
   * free (the same code, and no second audit row).
   */
  const printSticker = async (sticker: { itemName: string; barcode: string }) => {
    if (printingRef.current) return;
    const printer = itemStickerPrinter();
    if (!printer) {
      // Both lines go, not just the red one: a green "Printed." from the last
      // tap sitting above the pairing panel reads as a claim about this one.
      setActionError(null);
      setNotice(null);
      setPanel("print-barcode");
      return;
    }
    printingRef.current = true;
    setPrinting(true);
    setActionError(null);
    setNotice(null);
    try {
      const result = await printItemSticker(printer, sticker);
      // The card may have been closed while the printer was chewing on it.
      if (!onScreen.current) return;
      /**
       * WHICH MACHINE, when it is the one receipts come out of — the notes
       * role falls back to the receipts printer and comes back flagged
       * `shared`; that is the normal setup here, so it is where to walk, not
       * a warning. The words live in `itemStickerOutcome.ts` (G-130), shared
       * with the receiving row; the too-narrow note is reported after the
       * fact, never as a gate.
       */
      const words = itemStickerOutcomeText(result, printer);
      if (words.notice) setNotice(words.notice);
      if (words.error) setActionError(words.error);
    } finally {
      printingRef.current = false;
      if (onScreen.current) setPrinting(false);
    }
  };

  /**
   * G-99 — mint this shop's code for an item that has none. G-100 (owner
   * 2026-09-22) carries it through to the paper: issue, then print, on the one
   * tap. Leaving the old panel here would have kept the two-step alive on this
   * path alone, so the same sticker on the same card would behave two ways.
   *
   * Not routed through `run` above: that one closes the panel and writes its
   * own success line, and here the success is a sticker.
   *
   * The code is committed before any paper moves. A sticker on a shelf that
   * the database has never heard of is unexplainable to whoever scans it; a
   * code with no sticker is one more press of the same button — which is
   * exactly what the florist gets, because the row now offers "Print barcode".
   */
  const assignBarcode = async () => {
    // Read BEFORE the re-read below: `card` in this closure goes stale the
    // moment `load` replaces it, and the sticker needs the name.
    const itemName = card?.item.name;
    setAssigning(true);
    setActionError(null);
    setNotice(null);
    let issuedText: string | null = null;
    try {
      const issued = await shopModeApi.floorIssueItemBarcode(itemId);
      issuedText = issued.barcodeText;
      // Held BEFORE the re-read, so the sticker can still be printed if the
      // re-read is the thing that fails. `load` swallows its own errors.
      setJustIssued(issuedText);
      await load();
      onChanged?.();
    } catch (err: unknown) {
      setActionError(errorText(err, "itemCard.didNotGoThrough"));
    } finally {
      setAssigning(false);
    }
    if (issuedText && itemName && onScreen.current) {
      await printSticker({ itemName, barcode: issuedText });
    }
  };

  /**
   * Owner flow, 2026-09-16: a scanned item the shop does not carry is
   * RECEIVED (it just arrived); one it already carries has its quantity
   * ADJUSTED. The card leads with whichever matches the situation.
   */
  const stockedHere = Number(card?.stock.totalQtyOnHand ?? "0") > 0;

  /**
   * Where a receive lands unless the florist says otherwise: the front shop,
   * because goods they are holding go out on the floor. Falls back to the
   * shop's first zone when a shop has no front-of-house zone at all.
   */
  const defaultReceiveZoneId = (): string => {
    const zones = card?.shopZones ?? [];
    return (
      zones.find((z) => z.zoneType === "FRONT_SHOP")?.zoneId ??
      zones[0]?.zoneId ??
      ""
    );
  };

  /** What this item currently has in a zone — "0" for a zone it has never been in. */
  const qtyInZone = (zoneId: string): string =>
    card?.stock.zones.find((z) => z.zoneId === zoneId)?.qtyOnHand ?? "0";

  const canEdit = (field: string) =>
    card?.permissions.editableFields.includes(field) ?? false;

  /** G-121 S4 (OD-150): a purchase price on "Receive this item" is a manager's to type. */
  const canTypePrice = card?.permissions.canManage ?? false;

  // G-174: the supplier list, read once when a manager opens the receive panel.
  const receiveOpen = panel === "receive";
  useEffect(() => {
    if (!receiveOpen || !canTypePrice || floorSuppliers !== null) return;
    let cancelled = false;
    setSuppliersFailed(false);
    shopModeApi
      .floorListSuppliers()
      .then((rows) => {
        if (!cancelled) setFloorSuppliers(rows);
      })
      .catch(() => {
        if (!cancelled) setSuppliersFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [receiveOpen, canTypePrice, floorSuppliers]);

  const saveEdits = () => {
    if (!card) return;
    const payload: Parameters<typeof shopModeApi.floorUpdateItem>[1] = {};
    const nextDescription = descriptionDraft.trim();
    if (nextDescription !== (card.item.description ?? "")) {
      payload.description = nextDescription || null;
    }
    if (canEdit("name")) {
      const nextName = nameDraft.trim();
      if (nextName && nextName !== card.item.name) payload.name = nextName;
    }
    if (canEdit("defaultSaleUnitPriceEur")) {
      const typed = priceDraft.trim();
      const current = card.item.defaultSaleUnitPriceEur ?? "";
      if (typed !== current) {
        if (typed === "") {
          payload.defaultSaleUnitPriceEur = null;
        } else {
          const n = Number(typed);
          if (!Number.isFinite(n) || n < 0) {
            setActionError(t("itemCard.priceInvalid"));
            return;
          }
          payload.defaultSaleUnitPriceEur = n;
        }
      }
    }
    if (Object.keys(payload).length === 0) {
      setPanel("none");
      return;
    }
    void run(
      () => shopModeApi.floorUpdateItem(itemId, payload).then(() => undefined),
      t("itemCard.saved"),
    );
  };

  const saveQuantity = () => {
    const qty = Number(qtyDraft);
    if (!zoneDraft) {
      setActionError(t("itemCard.pickZone"));
      return;
    }
    if (!Number.isFinite(qty) || qty < 0) {
      setActionError(t("itemCard.qtyInvalid"));
      return;
    }
    // G-86: the reason is optional. A count is a fact on its own, and a
    // florist doing an opening stocktake should not have to write a sentence
    // per shelf; the server records a plain "Stock counted" when none is given.
    const reason = reasonDraft.trim();
    void run(
      () =>
        shopModeApi
          .floorAdjustStock({
            itemId,
            zoneId: zoneDraft,
            targetQtyOnHand: qty,
            ...(reason ? { reason } : {}),
          })
          .then(() => undefined),
      t("itemCard.qtyCorrected"),
    );
  };

  /**
   * G-86: stock that ARRIVED, as opposed to stock that was counted.
   *
   * The price is per unit because that is the number on the box a florist is
   * holding; the server is given the total. Left blank it is omitted entirely
   * and the server seeds the cost from the last real batch or the catalogue
   * default — an honest unknown instead of a receive booked at €0.
   */
  const saveReceive = () => {
    const qty = Number(recvQtyDraft);
    if (!recvZoneDraft) {
      setActionError(t("itemCard.pickZone"));
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      setActionError(t("itemCard.enterHowManyArrived"));
      return;
    }
    // G-121 S4 (OD-150): only a manager types a price; a florist receives by quantity.
    const typed = canTypePrice ? recvUnitPriceDraft.trim() : "";
    let priced: { invoiceTotalEur: number; moneyBasis: TypedMoneyBasis; supplierId?: string } | null = null;
    if (typed !== "") {
      const unit = Number(typed);
      if (!Number.isFinite(unit) || unit < 0) {
        setActionError(t("itemCard.priceMustBeNumber"));
        return;
      }
      // G-121 S3 (OD-138): a typed price has no default side of IVA.
      if (recvBasisDraft === null) {
        setActionError(t("itemCard.pickIvaBasis"));
        return;
      }
      // G-174 (KI-100): with a price, who sold it — or "Not on the list".
      if (recvSupplierDraft === "") {
        setActionError(t("itemCard.pickSupplier"));
        return;
      }
      priced = {
        invoiceTotalEur: invoiceTotalFromUnitPrice(unit, qty),
        moneyBasis: recvBasisDraft,
        ...(recvSupplierDraft !== NOT_ON_LIST ? { supplierId: recvSupplierDraft } : {}),
      };
    }
    let stored: ReceivedBatchMoney | null = null;
    void run(
      async () => {
        stored = await shopModeApi.floorReceiveStock({
          itemId,
          zoneId: recvZoneDraft,
          quantity: qty,
          ...(priced ?? {}),
        });
      },
      // The server's own split, so the florist sees the IVA taken out.
      () => {
        const batch: ReceivedBatchMoney | null = stored;
        return batch !== null && batch.ivaEur !== null && batch.grossTotalEur !== null
          ? translate("itemCard.receivedIvaSplit", {
              net: money(batch.invoiceTotalEur),
              iva: money(batch.ivaEur),
              gross: money(batch.grossTotalEur),
            })
          : translate("itemCard.receivedIntoStock");
      },
    );
  };

  /**
   * G-87: `PhotoInput` hands over the file the florist COMMITTED — the
   * background-removed PNG, or the original when they kept it or the sidecar
   * was down. Uploading is unchanged from there.
   */
  const onPhotoChosen = (file: File) => {
    void run(
      () =>
        shopModeApi.floorReplaceItemPhoto(itemId, file).then(() => undefined),
      t("itemCard.photoReplaced"),
    );
  };

  const onBarcodeScanned = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      setActionError(t("itemCard.scanNoDigits"));
      setPanel("none");
      return;
    }
    void run(async () => {
      const result = await shopModeApi.floorSetItemBarcode(itemId, digits);
      if (!result.changed) setNotice(t("itemCard.barcodeAlready"));
    }, t("itemCard.barcodeSaved"));
  };

  /**
   * G-84 (OD-50): the label was scanned. The scan is MANDATORY here — typing a
   * code is not offered — because the scan is what proves which physical box
   * received the item. An item barcode, or a box from another shop, is "not a
   * box label": stay on the question, offer the camera again.
   */
  const onBoxLabelScanned = (value: string) => {
    const code = value.trim();
    if (!code) return;
    setPutScanned(code);
    void (async () => {
      try {
        const box = await shopModeApi.floorFindBoxByBarcode(code);
        const shelves = shelfZonesWithStock();
        setPutBox(box);
        setPutQty("1");
        setPutZoneId(shelves.length === 1 ? shelves[0].zoneId : "");
        setPanel("put-box-qty");
      } catch (err: unknown) {
        if (hasStatus(err) && err.status === 404) {
          setPanel("put-box-notbox");
          return;
        }
        setActionError(errorText(err, "itemCard.couldNotReadLabel"));
        setPanel("none");
      }
    })();
  };

  /** Shelf zones (never boxes) where this item has stock: where it can be taken FROM. */
  const shelfZonesWithStock = () =>
    (card?.stock.zones ?? []).filter(
      (z) => isShelfZone(z) && Number(z.qtyOnHand) > 0,
    );
  const shelfQty = shelfZonesWithStock().reduce((sum, z) => sum + Number(z.qtyOnHand), 0);
  const putMax = putZoneId
    ? Number(shelfZonesWithStock().find((z) => z.zoneId === putZoneId)?.qtyOnHand ?? 0)
    : shelfQty;

  if (panel === "put-box-scan") {
    return createPortal(
      <div className="sf-bouquet-modal-backdrop" role="dialog" aria-modal="true" aria-label={t("itemCard.scanTheBox")}>
        <Suspense fallback={<div className="sf-bouquet-modal">{t("itemCard.startingCamera")}</div>}>
          <BarcodeScanner onDetected={onBoxLabelScanned} onClose={() => setPanel("none")} />
        </Suspense>
      </div>,
      document.body,
    );
  }

  if (panel === "put-box-notbox") {
    return createPortal(
      <div className="shop-floor-scan-flow sf-scan-overlay">
        <div className="shop-floor-scan-card">
          <p className="shop-floor-scan-title">{t("itemCard.notABoxLabel")}</p>
          <p className="muted">
            {putScanned
              ? `${t("itemCard.scannedValue", { code: putScanned })} `
              : ""}
            {t("itemCard.pointAtBoxBarcode")}
          </p>
          <div className="shop-floor-action-stack">
            <button type="button" className="button shop-floor-action-btn" onClick={() => setPanel("put-box-scan")}>
              {t("itemCard.scanAgain")}
            </button>
            <button type="button" className="button plain" onClick={() => setPanel("none")}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (panel === "scan") {
    return createPortal(
      <div className="sf-bouquet-modal-backdrop" role="dialog" aria-modal="true">
        <Suspense
          fallback={<div className="sf-bouquet-modal">{t("itemCard.startingCamera")}</div>}
        >
          <BarcodeScanner
            onDetected={onBarcodeScanned}
            onClose={() => setPanel("none")}
          />
        </Suspense>
      </div>,
      document.body,
    );
  }

  const item = card?.item;
  const printable = (item ? printableBarcode(item) : null) ?? justIssued;

  return createPortal(
    <div
      className="sf-bouquet-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={item ? item.name : t("itemCard.item")}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sf-bouquet-modal sf-item-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sf-item-card-head">
          <h4 className="sf-bouquet-modal-title">
            {loading ? t("common.loading") : (item?.name ?? t("itemCard.item"))}
          </h4>
          <button type="button" className="button plain" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>

        {error ? <div className="alert error">{error}</div> : null}
        {actionError ? <div className="alert error">{actionError}</div> : null}
        {notice ? <div className="alert success">{notice}</div> : null}

        {card && item ? (
          <div className="sf-item-card-body">
            <div className="sf-item-card-photo">
              {photoUrl ? (
                <img src={photoUrl} alt={item.name} />
              ) : (
                <div className="sf-item-card-nophoto">{t("itemCard.noPhoto")}</div>
              )}
              {/*
                G-87: the SAME photo flow the CRM uses — resize, background
                removal, and the side-by-side "keep this one or the original?"
                modal — instead of the two raw file inputs that used to post
                the camera's output straight up. `removeBackground` is injected
                because the staff route rejects a floor token; everything else
                about the component is shared, so the tablet and the desk
                cannot drift apart.
              */}
              <div className="sf-item-card-photo-actions">
                <PhotoInput
                  value={null}
                  label={item.name}
                  showRemoveButton={false}
                  removeBackground={shopModeApi.floorRemoveBackground}
                  onChange={(picked) => {
                    if (picked) onPhotoChosen(picked);
                  }}
                />
              </div>
            </div>

            <div className="sf-item-card-facts">
              {item.description ? (
                <p className="sf-item-card-description">{item.description}</p>
              ) : null}

              <dl className="sf-item-card-dl">
                <div>
                  <dt>{t("itemCard.inThisShop")}</dt>
                  <dd>
                    <strong>{card.stock.totalQtyOnHand}</strong>{" "}
                    {uomLabel(item.defaultUom, Number(card.stock.totalQtyOnHand))}
                  </dd>
                </div>
                <div>
                  <dt>{t("itemCard.price")}</dt>
                  <dd>
                    {money(item.saleUnitPriceEur)}
                    {item.saleUnitPriceIsDerived ? (
                      <>
                        {" "}
                        <span className="muted">{t("itemCard.workedOut")}</span>
                      </>
                    ) : null}
                  </dd>
                </div>
                {item.defaultPurchaseUnitCostEur !== undefined ? (
                  <div>
                    <dt>{t("itemCard.purchaseCost")}</dt>
                    <dd>
                      {item.defaultPurchaseUnitCostEur
                        ? cost(item.defaultPurchaseUnitCostEur)
                        : "—"}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>{t("itemCard.category")}</dt>
                  <dd>
                    {item.category.label}
                    {item.subCategory
                      ? ` · ${flowerTypeLabel(item.subCategory.code, item.subCategory.label)}`
                      : ""}
                  </dd>
                </div>
                {item.producer ? (
                  <div>
                    <dt>{t("itemCard.producer")}</dt>
                    <dd>{item.producer}</dd>
                  </div>
                ) : null}
                {item.color ? (
                  <div>
                    <dt>{t("itemCard.colour")}</dt>
                    <dd>{colourLabel(item.color.name)}</dd>
                  </div>
                ) : null}
                {item.stemHeightCm ? (
                  <div>
                    <dt>{t("itemCard.stemHeight")}</dt>
                    <dd>
                      {item.stemHeightCm} {uomLabel("CM")}
                    </dd>
                  </div>
                ) : null}
                {item.diameterCm || item.depthCm ? (
                  <div>
                    <dt>{t("itemCard.size")}</dt>
                    <dd>
                      {item.diameterCm ? t("itemCard.diameter", { cm: item.diameterCm }) : ""}
                      {item.diameterCm && item.depthCm ? " · " : ""}
                      {item.depthCm ? t("itemCard.depth", { cm: item.depthCm }) : ""}
                    </dd>
                  </div>
                ) : null}
                {item.sku ? (
                  <div>
                    <dt>SKU</dt>
                    <dd>{item.sku}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>{t("itemCard.barcode")}</dt>
                  <dd>{item.barcode ?? t("itemCard.noneYet")}</dd>
                </div>
                {/* G-99: shown only when the shop has issued one, and BESIDE
                    the barcode above rather than instead of it — an item can
                    carry both, and a florist needs to know which is which. */}
                {item.issuedBarcode ? (
                  <div>
                    <dt>{t("itemCard.shopCode")}</dt>
                    <dd>{item.issuedBarcode.code}</dd>
                  </div>
                ) : null}
              </dl>

              {card.stock.zones.length > 0 ? (
                <div className="sf-item-card-zones">
                  <p className="sf-item-card-subhead">{t("itemCard.whereItIs")}</p>
                  <ul>
                    {card.stock.zones.map((z) => (
                      <li key={z.zoneId}>
                        {/* G-84 (owner 2026-09-17): a place that is a box says
                            so, and says where the box is kept — "BOX-000017 ·
                            Storage". The code alone sent a florist hunting. */}
                        <span className={z.zoneType === "BOX" ? "sf-item-card-in-box" : undefined}>
                          {z.zoneType === "BOX" ? (
                            <>
                              {z.box?.code ?? z.zoneName}
                              {z.box?.keptInZoneName ? (
                                <span className="muted"> · {z.box.keptInZoneName}</span>
                              ) : null}
                            </>
                          ) : (
                            z.zoneName
                          )}
                        </span>
                        <strong>{z.qtyOnHand}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="sf-item-card-arrivals">
                <p className="sf-item-card-subhead">
                  {item.isPerishable
                    ? t("itemCard.batches")
                    : t("itemCard.latestArrivals")}
                </p>
                {card.arrivals.length === 0 ? (
                  <p className="muted">{t("itemCard.noArrivals")}</p>
                ) : (
                  <ul>
                    {card.arrivals.map((a) => (
                      <li key={a.id}>
                        <span className="sf-item-card-arrival-when">
                          {formatWhen(a.receivedAt)}
                        </span>
                        <span>
                          {a.supplierName ?? t("itemCard.noSupplier")}
                          {a.zoneName ? ` → ${a.zoneName}` : ""}
                        </span>
                        <span className="sf-item-card-arrival-qty">
                          {t("itemCard.remainingOf", {
                            remaining: a.remainingQty,
                            received: a.receivedQty,
                          })}
                          <ArrivalCost arrival={a} uom={item.defaultUom} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {card ? (
          <>
            {panel === "none" ? (
              <div className="sf-item-card-actions">
                <button
                  type="button"
                  className="button shop-floor-action-btn"
                  disabled={busy || printing}
                  onClick={() => setPanel("scan")}
                >
                  {card.item.barcode ? t("itemCard.changeBarcode") : t("itemCard.addBarcode")}
                </button>
                {/* G-84 follow-up (owner 2026-09-17): only when there is a
                    barcode to print — a dead button is the G-27 rule.
                    G-99 (owner 2026-09-21): an item with nothing to print gets
                    ONE button that says what it will do instead of a disabled
                    one. Two steps, two words: assign, then print.
                    G-100 (owner 2026-09-22): and now ONE tap — this prints,
                    rather than opening a screen with another button on it. */}
                {printable ? (
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy || printing}
                    onClick={() =>
                      void printSticker({
                        itemName: card.item.name,
                        barcode: printable,
                      })
                    }
                  >
                    {printing ? t("printer.printing") : t("itemCard.printBarcode")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy || assigning}
                    onClick={() => void assignBarcode()}
                  >
                    {/* No `printing` state here, and that is not an omission:
                        the issue sets `justIssued` before the print starts, so
                        by then `printable` is truthy and the branch ABOVE is
                        what the florist is looking at. It shows "Printing…"
                        for both paths. */}
                    {assigning
                      ? t("itemCard.assigningBarcode")
                      : t("itemCard.assignBarcode")}
                  </button>
                )}
                <button
                  type="button"
                  className="button shop-floor-action-btn"
                  disabled={busy || printing}
                  onClick={() => {
                    setActionError(null);
                    setPanel("edit");
                  }}
                >
                  {t("itemCard.editDetails")}
                </button>
                {card.quantityCorrection.allowed ? (
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy || printing}
                    onClick={() => {
                      setActionError(null);
                      // Prefer a zone the item is already in; otherwise the
                      // shop's first zone, so a never-stocked item still opens
                      // on something the florist can accept or change.
                      const zone =
                        card.stock.zones.find((z) => z.zoneId === zoneDraft) ??
                        card.stock.zones[0] ??
                        card.shopZones[0];
                      const zoneId =
                        "zoneId" in (zone ?? {}) ? (zone as { zoneId: string }).zoneId : "";
                      setZoneDraft(zoneId);
                      setQtyDraft(zoneId ? qtyInZone(zoneId) : "");
                      setReasonDraft("");
                      setPanel("quantity");
                    }}
                  >
                    {stockedHere ? t("itemCard.adjustQuantity") : t("itemCard.countItInstead")}
                  </button>
                ) : null}
                {/*
                  G-86: receiving is the other half — goods that ARRIVED, with
                  what they cost, rather than a shelf that was counted. Gated on
                  the same non-flower constraint as the count, because the
                  single-use receive door refuses perishables and flower
                  categories for the same reason: their quantity is carried by
                  FIFO batches. The server remains the authority on both.
                */}
                {card.quantityCorrection.allowed ? (
                  <button
                    type="button"
                    className={`button shop-floor-action-btn${
                      stockedHere ? "" : " sf-item-card-action-first"
                    }`}
                    disabled={busy || printing}
                    onClick={() => {
                      setActionError(null);
                      setRecvZoneDraft(defaultReceiveZoneId());
                      setRecvQtyDraft("");
                      // Prefilled only when this person is allowed to see cost
                      // at all (G-73 keeps purchase cost a manager's fact), so
                      // the card never reveals it to a florist by way of a
                      // convenience default.
                      setRecvUnitPriceDraft(card.item.defaultPurchaseUnitCostEur ?? "");
                      // G-121 S3: a stored cost is before IVA; an empty box has no side.
                      setRecvBasisDraft(basisForPrefill(card.item.defaultPurchaseUnitCostEur));
                      setRecvSupplierDraft("");
                      setPanel("receive");
                    }}
                  >
                    {stockedHere ? t("itemCard.receiveMore") : t("itemCard.receiveThisItem")}
                  </button>
                ) : null}
                {shelfQty > 0 ? (
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy || printing}
                    onClick={() => {
                      setActionError(null);
                      setPutBox(null);
                      setPutScanned(null);
                      setPanel("put-box-scan");
                    }}
                  >
                    {t("itemCard.putInTheBox")}
                  </button>
                ) : null}
              </div>
            ) : null}

            {/* G-100: reached only when no printer resolved at the tap — a
                browser, or nothing paired with this tablet. It is not a dead
                end: `PairPrinterPrompt` inside fixes exactly that, and prints
                once from here; the next tap on the card goes straight to
                paper. */}
            {panel === "print-barcode" && printable ? (
              <div className="sf-item-card-panel">
                <p className="sf-item-card-subhead">{t("itemCard.barcodeSticker")}</p>
                <ShopFloorItemBarcodePrint
                  itemName={card.item.name}
                  barcode={printable}
                />
                <div className="sf-item-card-panel-actions">
                  <button
                    type="button"
                    className="button plain shop-floor-action-btn"
                    onClick={() => setPanel("none")}
                  >
                    {t("itemCard.done")}
                  </button>
                </div>
              </div>
            ) : null}

            {panel === "put-box-qty" && putBox ? (
              <div className="sf-item-card-panel">
                <p className="sf-item-card-subhead">
                  Into {putBox.code}
                  {putBox.name ? ` · ${putBox.name}` : ""}
                </p>
                {shelfZonesWithStock().length > 1 ? (
                  <>
                    <label htmlFor="sf-put-zone">{t("itemCard.takingItFrom")}</label>
                    <select
                      id="sf-put-zone"
                      value={putZoneId}
                      onChange={(e) => setPutZoneId(e.target.value)}
                    >
                      <option value="">{t("itemCard.anywhereOnShelves")}</option>
                      {shelfZonesWithStock().map((z) => (
                        <option key={z.zoneId} value={z.zoneId}>
                          {z.zoneName} ({z.qtyOnHand})
                        </option>
                      ))}
                    </select>
                  </>
                ) : null}
                <label htmlFor="sf-put-qty">{t("itemCard.howMany")}</label>
                <input
                  id="sf-put-qty"
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  max={putMax}
                  step="any"
                  value={putQty}
                  onChange={(e) => setPutQty(e.target.value)}
                />
                <p className="muted">{putMax} on the shelf{putZoneId ? "" : "s"}.</p>
                <div className="sf-item-card-panel-actions">
                  <button type="button" className="button plain" disabled={busy} onClick={() => setPanel("none")}>
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy || !(Number(putQty) > 0) || Number(putQty) > putMax}
                    onClick={() =>
                      void run(async () => {
                        await shopModeApi.floorPutInBox(putBox.id, {
                          itemId,
                          quantity: Number(putQty),
                          ...(putZoneId ? { fromZoneId: putZoneId } : {}),
                        });
                      }, translate("itemCard.putQtyInBox", { qty: putQty, box: putBox.code }))
                    }
                  >
                    {t("itemCard.putInBox")}
                  </button>
                </div>
              </div>
            ) : null}

            {/* The honest refusal, in the florist's words — not a box that
                fails on save. The rule itself lives on the server. */}
            {panel === "none" && !card.quantityCorrection.allowed ? (
              <p className="muted sf-item-card-refusal">
                {card.quantityCorrection.refusal}
              </p>
            ) : null}

            {panel === "edit" ? (
              <div className="sf-item-card-panel">
                <label htmlFor="sf-item-description">{t("itemCard.notesLabel")}</label>
                <textarea
                  id="sf-item-description"
                  rows={3}
                  value={descriptionDraft}
                  maxLength={2000}
                  placeholder={t("itemCard.notesPlaceholder")}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                />

                {canEdit("name") ? (
                  <>
                    <label htmlFor="sf-item-name">{t("itemCard.name")}</label>
                    <input
                      id="sf-item-name"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                    />
                  </>
                ) : null}

                {canEdit("defaultSaleUnitPriceEur") ? (
                  <>
                    <label htmlFor="sf-item-price">
                      {t("itemCard.sellingPrice", {
                        unit: uomLabel(card.item.defaultUom, 1),
                      })}
                    </label>
                    <input
                      id="sf-item-price"
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      value={priceDraft}
                      onChange={(e) => setPriceDraft(e.target.value)}
                    />
                  </>
                ) : (
                  <p className="muted">{t("itemCard.managerOnly")}</p>
                )}

                <div className="sf-item-card-panel-actions">
                  <button
                    type="button"
                    className="button"
                    disabled={busy}
                    onClick={saveEdits}
                  >
                    {busy ? t("belowFloor.saving") : t("itemCard.save")}
                  </button>
                  <button
                    type="button"
                    className="button plain"
                    disabled={busy}
                    onClick={() => setPanel("none")}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : null}

            {panel === "receive" ? (
              <div className="sf-item-card-panel">
                <label htmlFor="sf-recv-zone">{t("itemCard.whereDoesItGo")}</label>
                <select
                  id="sf-recv-zone"
                  value={recvZoneDraft}
                  onChange={(e) => setRecvZoneDraft(e.target.value)}
                >
                  {card.shopZones.length === 0 ? (
                    <option value="">{t("itemCard.noActiveZones")}</option>
                  ) : null}
                  {card.shopZones.map((z) => (
                    <option key={z.zoneId} value={z.zoneId}>
                      {t("itemCard.zoneNow", { zone: z.zoneName, qty: qtyInZone(z.zoneId) })}
                    </option>
                  ))}
                </select>

                <label htmlFor="sf-recv-qty">{t("itemCard.howManyArrived")}</label>
                <input
                  id="sf-recv-qty"
                  type="number"
                  min={0.001}
                  step="any"
                  inputMode="decimal"
                  value={recvQtyDraft}
                  onChange={(e) => setRecvQtyDraft(e.target.value)}
                />

                {/* G-121 S4 (OD-150): the price box is a manager's — florists count. */}
                {canTypePrice ? (
                <>
                <label htmlFor="sf-recv-price">{t("itemCard.pricePerUnitOptional")}</label>
                <input
                  id="sf-recv-price"
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  placeholder={t("itemCard.leaveEmptyUsualPrice")}
                  value={recvUnitPriceDraft}
                  onChange={(e) => {
                    setRecvUnitPriceDraft(e.target.value);
                    setRecvBasisDraft((b) => basisAfterEdit(b, e.target.value));
                  }}
                />
                {/* G-121 S3: which side of IVA — nothing picked until the florist taps one. */}
                {recvUnitPriceDraft.trim() !== "" ? (
                  <div className="sf-iva-basis" role="group" aria-label={t("itemCard.ivaBasisGroup")}>
                    {(["BEFORE_IVA", "INCLUDES_IVA"] as const).map((basis) => (
                      <button
                        key={basis}
                        type="button"
                        className={`sf-tab-chip${recvBasisDraft === basis ? " sf-tab-chip--active" : ""}`}
                        aria-pressed={recvBasisDraft === basis}
                        disabled={busy}
                        onClick={() => {
                          setRecvBasisDraft(basis);
                          setActionError(null);
                        }}
                      >
                        {basis === "BEFORE_IVA"
                          ? t("itemCard.beforeIva")
                          : t("itemCard.includesIva", { rate: percentText(card.item.category.vatRatePercent) })}
                      </button>
                    ))}
                  </div>
                ) : null}
                {/* G-174 (KI-100, owner 2026-10-22): who sold it, with a price only. */}
                {recvUnitPriceDraft.trim() !== "" ? (
                  <>
                    <label htmlFor="sf-recv-supplier">{t("itemCard.supplier")}</label>
                    <select
                      id="sf-recv-supplier"
                      value={recvSupplierDraft}
                      disabled={busy}
                      onChange={(e) => {
                        setRecvSupplierDraft(e.target.value);
                        setActionError(null);
                      }}
                    >
                      <option value="" disabled>
                        {t("itemCard.chooseSupplier")}
                      </option>
                      {(floorSuppliers ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                      <option value={NOT_ON_LIST}>{t("itemCard.supplierNotOnList")}</option>
                    </select>
                    {suppliersFailed ? (
                      <p className="muted sf-item-card-refusal">{t("itemCard.suppliersUnavailable")}</p>
                    ) : null}
                  </>
                ) : null}
                <p className="muted sf-item-card-refusal">
                  {(() => {
                    if (recvUnitPriceDraft.trim() === "") return t("itemCard.noPriceNoSpend");
                    if (recvBasisDraft === null) return t("itemCard.pickIvaBasis");
                    // Quantity × the typed unit — the total of what was typed,
                    // on the side it was typed; the IVA itself is the server's.
                    const amount = money(
                      Number(recvQtyDraft) > 0 && Number.isFinite(Number(recvUnitPriceDraft))
                        ? Number(recvUnitPriceDraft) * Number(recvQtyDraft)
                        : 0,
                    );
                    return recvBasisDraft === "INCLUDES_IVA"
                      ? t("itemCard.totalWithIvaTakenOut", { amount })
                      : t("itemCard.totalBeforeIva", { amount });
                  })()}
                </p>
                </>
                ) : (
                  <p className="muted sf-item-card-refusal">{t("itemCard.noPriceNoSpend")}</p>
                )}

                <div className="sf-item-card-panel-actions">
                  <button
                    type="button"
                    className="button"
                    disabled={busy}
                    onClick={saveReceive}
                  >
                    {busy ? t("itemCard.receiving") : t("itemCard.receive")}
                  </button>
                  <button
                    type="button"
                    className="button plain"
                    disabled={busy}
                    onClick={() => setPanel("none")}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : null}

            {panel === "quantity" ? (
              <div className="sf-item-card-panel">
                <label htmlFor="sf-item-zone">{t("itemCard.whichZone")}</label>
                {/*
                  G-86: every zone in the shop, not only the ones this item is
                  already in. An item that is not stocked here has nothing in
                  `stock.zones`, and listing only those made counting a newly
                  scanned product impossible — the one case a florist most
                  needs it for. "0 now" is the honest reading for a zone the
                  item has never been in.
                */}
                <select
                  id="sf-item-zone"
                  value={zoneDraft}
                  onChange={(e) => {
                    const next = e.target.value;
                    setZoneDraft(next);
                    setQtyDraft(qtyInZone(next));
                  }}
                >
                  {card.shopZones.length === 0 ? (
                    <option value="">{t("itemCard.noActiveZones")}</option>
                  ) : null}
                  {card.shopZones.map((z) => (
                    <option key={z.zoneId} value={z.zoneId}>
                      {t("itemCard.zoneNow", { zone: z.zoneName, qty: qtyInZone(z.zoneId) })}
                    </option>
                  ))}
                </select>

                <label htmlFor="sf-item-qty">{t("itemCard.howMany")}</label>
                <input
                  id="sf-item-qty"
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  value={qtyDraft}
                  onChange={(e) => setQtyDraft(e.target.value)}
                />

                <label htmlFor="sf-item-reason">{t("itemCard.whyDifferent")}</label>
                <input
                  id="sf-item-reason"
                  value={reasonDraft}
                  placeholder={t("itemCard.reasonPlaceholder")}
                  onChange={(e) => setReasonDraft(e.target.value)}
                />

                <div className="sf-item-card-panel-actions">
                  <button
                    type="button"
                    className="button"
                    disabled={busy}
                    onClick={saveQuantity}
                  >
                    {busy ? t("belowFloor.saving") : t("itemCard.correctIt")}
                  </button>
                  <button
                    type="button"
                    className="button plain"
                    disabled={busy}
                    onClick={() => setPanel("none")}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * WO-B (OD-198): one arrival's unit cost — a manager's; "Estimated" when it
 * was borrowed, "Cost unknown" when there is none. Nothing for a florist, who
 * sees no cost fact at all (G-73: the server sends neither key to them).
 */
function ArrivalCost({
  arrival,
  uom,
}: {
  arrival: FloorItemCard["arrivals"][number];
  uom: FloorItemCard["item"]["defaultUom"];
}) {
  const lotCost = lotCostFigure(arrival);
  if (!lotCost) return null;
  return (
    <>
      {" · "}
      {lotCost.amount != null ? `${lotCost.amount}/${uomLabel(uom, 1)}` : null}
      {lotCost.amount != null && lotCost.pill != null ? " " : null}
      {lotCost.pill != null ? <span className="badge warning">{lotCost.pill}</span> : null}
    </>
  );
}
